import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  Icon,
  Image,
  PlainButton,
  Text,
  Tooltip,
  Truncated,
} from '@customerio/pluma-components/react';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import { useDraggable, useDroppable } from '@dnd-kit/react';
import clsx from 'clsx';
import plur from 'plur';
import { mergeRefs } from 'react-merge-refs';
import { useAnalytics } from '@/services/analytics';

import { type Asset } from '@/services/db/asset/entity';
import { isEmptyString } from '@/utils/string';

import { fileSize } from '../../utils';
import { useAssetActions } from '../hooks/use-asset-actions';
import { canDeleteAsset, getDeleteBlockedReason } from '../utils/asset-validation';
import { getCreatorName } from '../utils/get-creator-name';
import { isPdf } from '../utils/is-pdf';
import { FolderIcon } from './folder-icon';
import * as styles from './index.css';

type AssetGridRootProps = { children: React.ReactNode };

export const AssetGridRoot = ({ children, ...props }: AssetGridRootProps) => {
  return (
    <Grid columns="repeat(auto-fill, minmax(200px, 1fr))" gap="250" {...props}>
      {children}
    </Grid>
  );
};

export function AssetGridFolder({
  asset,
  className,
  onDelete,
  isChecked,
  onCheck,
  hasCheckedItems,
  isDraggingActive,
  ...props
}: {
  asset: Asset;
  onDelete?: (asset: Asset) => void;
  isChecked?: boolean;
  onCheck?: (checked: boolean, assetId: string) => void;
  hasCheckedItems?: boolean;
  isDraggingActive?: boolean;
} & React.ComponentPropsWithoutRef<typeof PlainButton>) {
  const draggable = useDraggable({ id: asset.id });
  const droppable = useDroppable({ id: asset.id, disabled: !asset.is_dir });

  const isDragging = draggable.isDragSource;
  const isOver = droppable.isDropTarget;
  const creatorName = getCreatorName(asset);

  const { handleDelete } = useAssetActions({ onDelete });

  const shouldShowOpacity = isDragging === true || (isDraggingActive === true && isChecked === true);
  const style = shouldShowOpacity ? { opacity: 0.5 } : undefined;

  return (
    <PlainButton
      {...props}
      ref={mergeRefs([draggable.ref, droppable.ref])}
      textAlign="left"
      className={clsx(
        className,
        styles.assetGridWrapper,
        styles.assetGridFolder,
        isOver === true && styles.assetGridFolderDropping,
        isChecked === true && styles.assetGridChecked,
      )}
      style={style}
    >
      <Box position="relative" w="full" p="150">
        {onCheck != null ? (
          <Box
            position="absolute"
            style={{ top: '4px', left: '4px', zIndex: 1 }}
            className={clsx(styles.assetGridCheckbox, hasCheckedItems === true && styles.assetGridCheckboxVisible)}
          >
            <Checkbox
              name={`folder-${asset.id}`}
              ariaLabel="Select folder"
              isChecked={isChecked ?? false}
              onCheckedChange={(checked) => {
                onCheck(checked === true, asset.id);
              }}
              onClick={(event: React.MouseEvent) => {
                event.stopPropagation();
              }}
            />
          </Box>
        ) : null}
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow="1" w="full">
          <FolderIcon className={styles.folderIconGrid} />
        </Box>
      </Box>
      <Box as="span" display="block" px="150" pt="150" pb="050" w="full">
        <Truncated size="s" weight="semibold">
          {asset.name}
        </Truncated>
      </Box>

      <Box px="150" pb="150" w="full">
        <Flex alignItems="center" gap="050" mb="100">
          <Text size="xs">
            {asset.size} {plur('item', asset.size)}
          </Text>
          <Tooltip
            isCustom
            content={`Created ${formatDateTime(asset.created_at, 'medium')}${
              !isEmptyString(creatorName) ? ` by ${creatorName}` : ''
            }`}
          >
            {(context) => (
              <Box
                {...context.reference.getReferenceProps()}
                ref={context.reference.setReference}
                display="inline-flex"
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                }}
              >
                <Icon name="help" size="sm" color="subtle" />
              </Box>
            )}
          </Tooltip>
        </Flex>

        {onDelete != null ? (
          <Flex justifyContent="flex-end" className={styles.assetGridActions}>
            <Tooltip isCustom content={getDeleteBlockedReason(asset) ?? 'Delete folder'}>
              {(context) => (
                <Button
                  {...context.reference.getReferenceProps()}
                  ref={context.reference.setReference}
                  variant="subtle"
                  size="sm"
                  icon="delete-trash"
                  isIconOnly
                  isDanger
                  aria-label="Delete folder"
                  isDisabled={!canDeleteAsset(asset)}
                  onClick={(event) => handleDelete(asset, event)}
                />
              )}
            </Tooltip>
          </Flex>
        ) : null}
      </Box>
    </PlainButton>
  );
}

export function AssetGridAsset({
  asset,
  className,
  onDelete,
  isChecked,
  onCheck,
  unselectableReason,
  hasCheckedItems,
  isDraggingActive,
  ...props
}: {
  asset: Asset;
  onDelete?: (asset: Asset) => void;
  isChecked?: boolean;
  onCheck?: (checked: boolean, assetId: string) => void;
  unselectableReason?: string;
  hasCheckedItems?: boolean;
  isDraggingActive?: boolean;
} & React.ComponentPropsWithoutRef<typeof PlainButton>) {
  const { track } = useAnalytics();
  const { handleCopyUrl, handleDelete, handleOptimize } = useAssetActions({
    onDelete,
  });

  const draggable = useDraggable({
    id: asset.id,
    disabled: unselectableReason != null,
  });

  const isDragging = draggable.isDragSource;
  const creatorName = getCreatorName(asset);
  const size = fileSize(asset.size);

  const handleClick = (event: React.MouseEvent) => {
    if (unselectableReason != null) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    props.onClick?.(event);
  };

  const shouldShowOpacity = isDragging === true || (isDraggingActive === true && isChecked === true);
  const isClickable = props.onClick != null;
  const style = shouldShowOpacity ? { opacity: 0.5 } : undefined;

  return (
    <Tooltip isCustom content={unselectableReason ?? ''} isDisabled={unselectableReason == null}>
      {(context) => (
        <PlainButton
          {...props}
          {...context.reference.getReferenceProps()}
          ref={mergeRefs([draggable.ref, context.reference.setReference])}
          onClick={handleClick}
          textAlign="left"
          className={clsx(
            className,
            styles.assetGridWrapper,
            unselectableReason != null ? styles.assetUnselectable : undefined,
            !isClickable && styles.assetNotClickable,
            isChecked === true && styles.assetGridChecked,
          )}
          style={style}
        >
          <Box position="relative" w="full" p="150">
            {onCheck != null ? (
              <Box
                position="absolute"
                style={{ top: '4px', left: '4px', zIndex: 1 }}
                className={clsx(styles.assetGridCheckbox, hasCheckedItems === true && styles.assetGridCheckboxVisible)}
              >
                <Checkbox
                  name={`asset-${asset.id}`}
                  ariaLabel="Select asset"
                  isChecked={isChecked ?? false}
                  onCheckedChange={(checked) => {
                    onCheck(checked === true, asset.id);
                  }}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                  }}
                />
              </Box>
            ) : null}
            {isPdf(asset) ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                backgroundColor="active"
                className={clsx(styles.imagePreview, unselectableReason != null && styles.assetUnselectableContent)}
              >
                <Icon name="file-pdf" size="xl" />
              </Box>
            ) : (
              <Box className={styles.imagePreviewContainer}>
                <Image
                  loading="lazy"
                  src={asset.path}
                  className={clsx(styles.imagePreview, unselectableReason != null && styles.assetUnselectableContent)}
                />
              </Box>
            )}
          </Box>

          <Box
            as="span"
            display="block"
            px="150"
            pt="150"
            pb="050"
            w="full"
            className={unselectableReason != null ? styles.assetUnselectableContent : undefined}
          >
            <Truncated size="s" weight="semibold">
              {asset.name}
            </Truncated>
          </Box>

          <Box
            px="150"
            pb="150"
            w="full"
            className={unselectableReason != null ? styles.assetUnselectableContent : undefined}
          >
            <Flex alignItems="center" gap="050" mb="100">
              <Text size="xs">{size}</Text>
              <Tooltip
                isCustom
                content={`Created ${formatDateTime(asset.created_at, 'medium')}${
                  !isEmptyString(creatorName) ? ` by ${creatorName}` : ''
                }`}
              >
                {(context) => (
                  <Box
                    {...context.reference.getReferenceProps()}
                    ref={context.reference.setReference}
                    display="inline-flex"
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                    }}
                  >
                    <Icon name="help" size="sm" color="subtle" />
                  </Box>
                )}
              </Tooltip>
            </Flex>

            <Flex justifyContent="flex-end" className={styles.assetGridActions}>
              <Tooltip isCustom content="Copy URL">
                {(context) => (
                  <Button
                    {...context.reference.getReferenceProps()}
                    ref={context.reference.setReference}
                    variant="subtle"
                    size="sm"
                    icon="link"
                    isIconOnly
                    aria-label="Copy URL"
                    onClick={(event) => handleCopyUrl(asset, event)}
                  />
                )}
              </Tooltip>
              <Tooltip isCustom content="Open in new tab">
                {(context) => (
                  <Button
                    {...context.reference.getReferenceProps()}
                    ref={context.reference.setReference}
                    variant="subtle"
                    size="sm"
                    icon="view-on"
                    isIconOnly
                    aria-label="Open in new tab"
                    href={asset.path}
                    isExternal
                    onClick={(event: React.MouseEvent) => {
                      event.stopPropagation();
                    }}
                  />
                )}
              </Tooltip>
              {!isPdf(asset) ? (
                <Tooltip isCustom content="Optimize Image File">
                  {(context) => (
                    <Button
                      {...context.reference.getReferenceProps()}
                      ref={context.reference.setReference}
                      variant="subtle"
                      size="sm"
                      icon="collapse"
                      isIconOnly
                      aria-label="Optimize Image File"
                      onClick={(event) => {
                        handleOptimize(asset, event);
                        track('assetOptimizer', 'optimizeImageFile', {
                          'Builder Type': 'design-studio',
                        });
                      }}
                    />
                  )}
                </Tooltip>
              ) : null}
              {onDelete != null ? (
                <Tooltip isCustom content={getDeleteBlockedReason(asset) ?? 'Delete asset'}>
                  {(context) => (
                    <Button
                      {...context.reference.getReferenceProps()}
                      ref={context.reference.setReference}
                      variant="subtle"
                      size="sm"
                      icon="delete-trash"
                      isIconOnly
                      isDanger
                      aria-label="Delete asset"
                      isDisabled={!canDeleteAsset(asset)}
                      onClick={(event) => handleDelete(asset, event)}
                    />
                  )}
                </Tooltip>
              ) : null}
            </Flex>
          </Box>
        </PlainButton>
      )}
    </Tooltip>
  );
}

type AssetGridProps = {
  assets: Asset[];
  onFolderClick: (asset: Asset) => void;
  onAssetClick?: (asset: Asset) => void;
  onDelete?: (asset: Asset) => void;
  checkedAssetIds?: Set<string>;
  onCheck?: (checked: boolean, assetId: string) => void;
  unselectableAssetReasons?: Record<string, string>;
  isDraggingActive?: boolean;
};

export function AssetGrid({
  assets,
  onFolderClick,
  onAssetClick,
  onDelete,
  checkedAssetIds = new Set(),
  onCheck,
  unselectableAssetReasons = {},
  isDraggingActive,
}: AssetGridProps) {
  return (
    <AssetGridRoot>
      {assets.map((asset) =>
        asset.is_dir ? (
          <AssetGridFolder
            key={asset.id}
            asset={asset}
            onClick={() => onFolderClick(asset)}
            onDelete={onDelete}
            isChecked={checkedAssetIds.has(asset.id)}
            onCheck={onCheck}
            hasCheckedItems={checkedAssetIds.size > 0}
            isDraggingActive={isDraggingActive}
            data-test-id={`select-hydra-asset-modal-folder-${asset.id}`}
          />
        ) : (
          <AssetGridAsset
            key={asset.id}
            asset={asset}
            onClick={onAssetClick != null ? () => onAssetClick(asset) : undefined}
            onDelete={onDelete}
            isChecked={checkedAssetIds.has(asset.id)}
            onCheck={onCheck}
            unselectableReason={unselectableAssetReasons[asset.id]}
            hasCheckedItems={checkedAssetIds.size > 0}
            isDraggingActive={isDraggingActive}
            data-test-id={`select-hydra-asset-modal-asset-${asset.id}`}
          />
        ),
      )}
    </AssetGridRoot>
  );
}
